import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Home, Building2, FileText, Scale, Users, Star } from 'lucide-react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { formatPrice } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propertiesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/properties?featured=true&limit=3`),
          axios.get(`${API}/testimonials`)
        ]);
        setFeaturedProperties(propertiesRes.data);
        setTestimonials(testimonialsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: <Home className="w-8 h-8" />,
      title: 'Buying & Selling',
      description: 'Expert guidance for flats, villas, apartments, and land with verified properties and transparent transactions.'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'Property Management',
      description: 'Complete property care for NRIs and investors with regular inspections and hassle-free maintenance.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Real Estate Consultancy',
      description: 'Smart investment strategies with market insights and location analysis for profitable decisions.'
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Document Services',
      description: 'Smooth DTCP & CMDA approval process with assistance for registration and property updates.'
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: 'Legal Assistance',
      description: 'Expert legal support for property disputes, title verification, and protecting ownership rights.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://cdn.coverr.co/videos/coverr-aerial-view-of-buildings-in-the-city-7811/1080p.mp4" type="video/mp4" />
            <source src="https://static.videezy.com/system/resources/previews/000/005/529/original/city11.mp4" type="video/mp4" />
          </video>
          {/* Fallback Image */}
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt="Modern City Development"
            className="w-full h-full object-cover"
            style={{ position: 'absolute', top: 0, left: 0, zIndex: -1 }}
          />
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Navigation Inside Video */}
        <Navbar transparent={true} />

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <h1 className="font-heading text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] mb-6 text-[#F5F0E8] premium-glow">
              Invest for the Lifetime of Riches
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#E8DCC8] mb-8 max-w-3xl mx-auto nav-glow">
              Your trusted real estate partner in Tamil Nadu. Complete solutions from buying and selling to legal support and property management.
            </p>
            <Link
              to="/properties"
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D97706] to-[#F59E0B] text-white px-8 py-4 rounded-lg font-medium text-lg hover:from-[#B45309] hover:to-[#D97706] transition-all duration-300 hover:-translate-y-1 shadow-2xl"
            >
              Explore Properties
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-primary mb-4">
              Featured Properties
            </h2>
            <p className="text-lg leading-relaxed text-[#57534E] max-w-2xl mx-auto">
              Handpicked premium properties offering the best value and location
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredProperties.map((property, index) => (
                <motion.div
                  key={property.property_id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link
                    to={`/properties/${property.property_id}`}
                    data-testid={`property-card-${property.property_id}`}
                    className="property-card group block bg-white border border-stone-100 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:border-primary/20 rounded-sm"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={property.images[0] || 'https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&q=85'}
                        alt={property.title}
                        className="property-card-image w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4 bg-accent text-white px-4 py-1.5 rounded-sm text-sm font-medium">
                        Featured
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-2xl font-medium text-primary mb-2">
                        {property.title}
                      </h3>
                      <p className="text-[#57534E] mb-4">{property.location}, {property.city}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-heading font-medium text-primary">
                          {formatPrice(property.price)}
                        </span>
                        <span className="text-accent font-medium flex items-center gap-1">
                          View Details
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#57534E]">No featured properties available at the moment.</p>
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/properties"
              data-testid="view-all-properties"
              className="inline-flex items-center gap-2 border-2 border-primary text-primary px-8 py-3 rounded-sm font-medium hover:bg-primary hover:text-white transition-all duration-300"
            >
              View All Properties
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F0FDF4]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-primary mb-4">
              Our Core Services
            </h2>
            <p className="text-lg leading-relaxed text-[#57534E] max-w-2xl mx-auto">
              Complete real estate solutions tailored to your needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 bg-[#FFFBF5] hover:bg-white transition-colors duration-300 border border-transparent hover:border-stone-200 rounded-sm"
              >
                <div className="w-16 h-16 bg-secondary rounded-sm flex items-center justify-center text-primary mb-6">
                  {service.icon}
                </div>
                <h3 className="font-heading text-2xl font-medium text-primary mb-3">
                  {service.title}
                </h3>
                <p className="text-[#57534E] leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="view-all-services"
              className="inline-flex items-center gap-2 border-2 border-primary text-primary px-8 py-3 rounded-sm font-medium hover:bg-primary hover:text-white transition-all duration-300"
            >
              Learn More About Our Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-primary mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg leading-relaxed text-[#57534E] max-w-2xl mx-auto">
              Trusted by hundreds of satisfied homebuyers and investors
            </p>
          </motion.div>

          {testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.testimonial_id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-8 border border-stone-100 rounded-sm"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-[#57534E] leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4">
                    {testimonial.image && (
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="font-medium text-primary">{testimonial.name}</p>
                      <p className="text-sm text-[#57534E]">{testimonial.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#57534E]">No testimonials available yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mb-6">
              Ready to Start Your Real Estate Journey?
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-8">
              Contact us today and let's make your property goals a reality.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="inline-flex items-center gap-2 bg-accent text-white px-8 py-4 rounded-lg font-medium text-lg hover:bg-accent/90 transition-all duration-300 hover:-translate-y-1 shadow-lg"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}